import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { useAuthContext } from "../../context/authContext";
import { useApiHeaders } from "../../hooks/useApiHeaders";
import { PaginationQuery } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  Admin3DModelEntityType,
  Admin3DModelRecord,
  Admin3DModelUploadEntry,
  Admin3DModelUploadResult,
  AdminFastener,
  AdminFastenerUsage,
  AdminGammaMValue,
  AdminKmodValue,
  AdminLoadDurationClass,
  AdminLock,
  AdminLockAccessory,
  AdminLockLockstopMapping,
  AdminLockPairLockstopMapping,
  AdminLockModule,
  AdminLockModuleCoefficient,
  AdminLockStop,
  AdminMaterialGrade,
  AdminServiceClass,
  AdminStandard,
  NormativeType,
} from "./types";

const BASE = "/admin/modules/lock_calculator";

export const useAdminLockCalculatorApi = () => {
  const api = useApi();
  const { expire } = useAuthContext();
  const apiHeaders = useApiHeaders();

  // ── Standards ──

  const useStandards = (query: PaginationQuery, signal: AbortSignal) => {
    const url = buildUrl(`${BASE}/standards`, query);
    return useSWR<{ items: AdminStandard[]; total: number }, CustomError, string>(url, (url: string) =>
      api.get<{ items: AdminStandard[]; total: number }>({ url, signal }),
    );
  };

  const getStandards = useCallback(
    async (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`${BASE}/standards`, query);
      return api.get<{ items: AdminStandard[]; total: number }>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertStandard = useCallback(
    async (body: {
      code: string;
      name: string;
      nameIt?: string | null;
      nameDe?: string | null;
      nameFr?: string | null;
      nameEs?: string | null;
      normativeType: NormativeType;
      jurisdictionCode?: string | null;
      visibility?: string | null;
    }): Promise<{ id: string }> => {
      const url = `${BASE}/standards`;
      const result = await api.post<
        { id: string },
        {
          code: string;
          name: string;
          nameIt?: string | null;
          nameDe?: string | null;
          nameFr?: string | null;
          nameEs?: string | null;
          normativeType: NormativeType;
          jurisdictionCode?: string | null;
          visibility?: string | null;
        }
      >(url, body);
      mutateCache([`${BASE}/standards`, `${BASE.replace("/admin", "")}/standards`], url);
      return result;
    },
    [api],
  );

  const getStandard = useCallback(
    (id: string, signal: AbortSignal) => {
      return useSWRMutation<AdminStandard, CustomError, string>(`${BASE}/standards/${id}`, (url: string) =>
        api.get<AdminStandard>({ url, signal }),
      );
    },
    [api],
  );

  const updateStandard = useCallback(
    async (
      id: string,
      body: {
        code?: string | null;
        name?: string | null;
        nameIt?: string | null;
        nameDe?: string | null;
        nameFr?: string | null;
        nameEs?: string | null;
        normativeType?: NormativeType | null;
        jurisdictionCode?: string | null;
        visibility?: string | null;
      },
    ): Promise<void> => {
      const url = `${BASE}/standards/${id}`;
      await api.put<{
        code?: string | null;
        name?: string | null;
        nameIt?: string | null;
        nameDe?: string | null;
        nameFr?: string | null;
        nameEs?: string | null;
        normativeType?: NormativeType | null;
        jurisdictionCode?: string | null;
        visibility?: string | null;
      }>(url, body);
      mutateCache([`${BASE}/standards`, `${BASE.replace("/admin", "")}/standards`], url);
    },
    [api],
  );

  const updateStandardOrder = useCallback(
    async (id: string, body: { order: number }): Promise<void> => {
      const url = `${BASE}/standards/${id}/order`;
      await api.put(url, body);
      mutateCache([`${BASE}/standards`], url);
    },
    [api],
  );

  const deleteStandard = useCallback(
    async (id: string): Promise<void> => {
      await api.del(`${BASE}/standards/${id}`);
      mutateCache([`${BASE}/standards`, `${BASE.replace("/admin", "")}/standards`]);
    },
    [api],
  );

  // ── Service Classes ──

  const useServiceClasses = (query: PaginationQuery, signal: AbortSignal) => {
    const url = buildUrl(`${BASE}/service_classes`, query);
    return useSWR<{ items: AdminServiceClass[]; total: number }, CustomError, string>(url, (url: string) =>
      api.get<{ items: AdminServiceClass[]; total: number }>({ url, signal }),
    );
  };

  const getServiceClasses = useCallback(
    async (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`${BASE}/service_classes`, query);
      return api.get<{ items: AdminServiceClass[]; total: number }>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertServiceClass = useCallback(
    async (body: {
      classNumber: number;
      name: string;
      nameIt?: string | null;
      nameDe?: string | null;
      nameFr?: string | null;
      nameEs?: string | null;
      description?: string | null;
      descriptionIt?: string | null;
      descriptionDe?: string | null;
      descriptionFr?: string | null;
      descriptionEs?: string | null;
      typicalExamples?: string | null;
      order?: number | null;
    }): Promise<{ id: string }> => {
      const url = `${BASE}/service_classes`;
      const result = await api.post<
        { id: string },
        {
          classNumber: number;
          name: string;
          nameIt?: string | null;
          nameDe?: string | null;
          nameFr?: string | null;
          nameEs?: string | null;
          description?: string | null;
          descriptionIt?: string | null;
          descriptionDe?: string | null;
          descriptionFr?: string | null;
          descriptionEs?: string | null;
          typicalExamples?: string | null;
          order?: number | null;
        }
      >(url, body);
      mutateCache([`${BASE}/service_classes`, `${BASE.replace("/admin", "")}/service_classes`], url);
      return result;
    },
    [api],
  );

  const getServiceClass = useCallback(
    (id: string, signal: AbortSignal) => {
      return useSWRMutation<AdminServiceClass, CustomError, string>(`${BASE}/service_classes/${id}`, (url: string) =>
        api.get<AdminServiceClass>({ url, signal }),
      );
    },
    [api],
  );

  const updateServiceClass = useCallback(
    async (
      id: string,
      body: {
        classNumber?: number | null;
        name?: string | null;
        nameIt?: string | null;
        nameDe?: string | null;
        nameFr?: string | null;
        nameEs?: string | null;
        description?: string | null;
        descriptionIt?: string | null;
        descriptionDe?: string | null;
        descriptionFr?: string | null;
        descriptionEs?: string | null;
        typicalExamples?: string | null;
        order?: number | null;
      },
    ): Promise<void> => {
      const url = `${BASE}/service_classes/${id}`;
      await api.put<{
        classNumber?: number | null;
        name?: string | null;
        nameIt?: string | null;
        nameDe?: string | null;
        nameFr?: string | null;
        nameEs?: string | null;
        description?: string | null;
        descriptionIt?: string | null;
        descriptionDe?: string | null;
        descriptionFr?: string | null;
        descriptionEs?: string | null;
        typicalExamples?: string | null;
        order?: number | null;
      }>(url, body);
      mutateCache([`${BASE}/service_classes`, `${BASE.replace("/admin", "")}/service_classes`], url);
    },
    [api],
  );

  const deleteServiceClass = useCallback(
    async (id: string): Promise<void> => {
      await api.del(`${BASE}/service_classes/${id}`);
      mutateCache([`${BASE}/service_classes`, `${BASE.replace("/admin", "")}/service_classes`]);
    },
    [api],
  );

  // ── Load Duration Classes ──

  const useLoadDurationClasses = (query: PaginationQuery, signal: AbortSignal) => {
    const url = buildUrl(`${BASE}/load_duration_classes`, query);
    return useSWR<{ items: AdminLoadDurationClass[]; total: number }, CustomError, string>(url, (url: string) =>
      api.get<{ items: AdminLoadDurationClass[]; total: number }>({ url, signal }),
    );
  };

  const getLoadDurationClasses = useCallback(
    async (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`${BASE}/load_duration_classes`, query);
      return api.get<{ items: AdminLoadDurationClass[]; total: number }>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertLoadDurationClass = useCallback(
    async (body: {
      code: string;
      name: string;
      nameIt?: string | null;
      nameDe?: string | null;
      nameFr?: string | null;
      nameEs?: string | null;
      typicalDuration?: string | null;
      typicalLoads?: string | null;
      order?: number | null;
    }): Promise<{ id: string }> => {
      const url = `${BASE}/load_duration_classes`;
      const result = await api.post<
        { id: string },
        {
          code: string;
          name: string;
          nameIt?: string | null;
          nameDe?: string | null;
          nameFr?: string | null;
          nameEs?: string | null;
          typicalDuration?: string | null;
          typicalLoads?: string | null;
          order?: number | null;
        }
      >(url, body);
      mutateCache([`${BASE}/load_duration_classes`, `${BASE.replace("/admin", "")}/load_duration_classes`], url);
      return result;
    },
    [api],
  );

  const getLoadDurationClass = useCallback(
    (id: string, signal: AbortSignal) => {
      return useSWRMutation<AdminLoadDurationClass, CustomError, string>(
        `${BASE}/load_duration_classes/${id}`,
        (url: string) => api.get<AdminLoadDurationClass>({ url, signal }),
      );
    },
    [api],
  );

  const updateLoadDurationClass = useCallback(
    async (
      id: string,
      body: {
        code?: string | null;
        name?: string | null;
        nameIt?: string | null;
        nameDe?: string | null;
        nameFr?: string | null;
        nameEs?: string | null;
        typicalDuration?: string | null;
        typicalLoads?: string | null;
        order?: number | null;
      },
    ): Promise<void> => {
      const url = `${BASE}/load_duration_classes/${id}`;
      await api.put<{
        code?: string | null;
        name?: string | null;
        nameIt?: string | null;
        nameDe?: string | null;
        nameFr?: string | null;
        nameEs?: string | null;
        typicalDuration?: string | null;
        typicalLoads?: string | null;
        order?: number | null;
      }>(url, body);
      mutateCache([`${BASE}/load_duration_classes`, `${BASE.replace("/admin", "")}/load_duration_classes`], url);
    },
    [api],
  );

  const deleteLoadDurationClass = useCallback(
    async (id: string): Promise<void> => {
      await api.del(`${BASE}/load_duration_classes/${id}`);
      mutateCache([`${BASE}/load_duration_classes`, `${BASE.replace("/admin", "")}/load_duration_classes`]);
    },
    [api],
  );

  // ── Material Grades ──

  const useMaterialGrades = (query: PaginationQuery, signal: AbortSignal) => {
    const url = buildUrl(`${BASE}/material_grades`, query);
    return useSWR<{ items: AdminMaterialGrade[]; total: number }, CustomError, string>(url, (url: string) =>
      api.get<{ items: AdminMaterialGrade[]; total: number }>({ url, signal }),
    );
  };

  const getMaterialGrades = useCallback(
    async (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`${BASE}/material_grades`, query);
      return api.get<{ items: AdminMaterialGrade[]; total: number }>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertMaterialGrade = useCallback(
    async (body: {
      code: string;
      name: string;
      standardReference?: string | null;
      rhoK?: number | null;
      rhoM?: number | null;
      fc0K?: number | null;
      fc90K?: number | null;
      fvK?: number | null;
      faxK?: number | null;
      kw?: number | null;
      aCoef?: number | null;
      bCoef?: number | null;
      visibility?: string | null;
    }): Promise<{ id: string }> => {
      const url = `${BASE}/material_grades`;
      const result = await api.post<
        { id: string },
        {
          code: string;
          name: string;
          standardReference?: string | null;
          rhoK?: number | null;
          rhoM?: number | null;
          fc0K?: number | null;
          fc90K?: number | null;
          fvK?: number | null;
          faxK?: number | null;
          kw?: number | null;
          aCoef?: number | null;
          bCoef?: number | null;
          visibility?: string | null;
        }
      >(url, body);
      mutateCache([`${BASE}/material_grades`, `${BASE.replace("/admin", "")}/material_grades`], url);
      return result;
    },
    [api],
  );

  const getMaterialGrade = useCallback(
    (id: string, signal: AbortSignal) => {
      return useSWRMutation<AdminMaterialGrade, CustomError, string>(`${BASE}/material_grades/${id}`, (url: string) =>
        api.get<AdminMaterialGrade>({ url, signal }),
      );
    },
    [api],
  );

  const updateMaterialGrade = useCallback(
    async (
      id: string,
      body: {
        code?: string | null;
        name?: string | null;
        standardReference?: string | null;
        rhoK?: number | null;
        rhoM?: number | null;
        fc0K?: number | null;
        fc90K?: number | null;
        fvK?: number | null;
        faxK?: number | null;
        kw?: number | null;
        aCoef?: number | null;
        bCoef?: number | null;
        visibility?: string | null;
      },
    ): Promise<void> => {
      const url = `${BASE}/material_grades/${id}`;
      await api.put<{
        code?: string | null;
        name?: string | null;
        standardReference?: string | null;
        rhoK?: number | null;
        rhoM?: number | null;
        fc0K?: number | null;
        fc90K?: number | null;
        fvK?: number | null;
        faxK?: number | null;
        kw?: number | null;
        aCoef?: number | null;
        bCoef?: number | null;
        visibility?: string | null;
      }>(url, body);
      mutateCache([`${BASE}/material_grades`, `${BASE.replace("/admin", "")}/material_grades`], url);
    },
    [api],
  );

  const deleteMaterialGrade = useCallback(
    async (id: string): Promise<void> => {
      await api.del(`${BASE}/material_grades/${id}`);
      mutateCache([`${BASE}/material_grades`, `${BASE.replace("/admin", "")}/material_grades`]);
    },
    [api],
  );

  const updateMaterialGradeOrder = useCallback(
    async (id: string, body: { order: number }): Promise<void> => {
      const url = `${BASE}/material_grades/${id}/order`;
      await api.put(url, body);
      mutateCache([`${BASE}/material_grades`], url);
    },
    [api],
  );

  // ── Fasteners ──

  const useFasteners = (query: PaginationQuery, signal: AbortSignal) => {
    const url = buildUrl(`${BASE}/fasteners`, query);
    return useSWR<{ items: AdminFastener[]; total: number }, CustomError, string>(url, (url: string) =>
      api.get<{ items: AdminFastener[]; total: number }>({ url, signal }),
    );
  };

  const getFasteners = useCallback(
    async (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`${BASE}/fasteners`, query);
      return api.get<{ items: AdminFastener[]; total: number }>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertFastener = useCallback(
    async (body: {
      itemId: string;
      diameter: number;
      length: number;
      threadLength?: number | null;
      myRk?: number | null;
      ftensK?: number | null;
      headType?: string | null;
      visibility?: string | null;
    }): Promise<{ id: string }> => {
      const url = `${BASE}/fasteners`;
      const result = await api.post<
        { id: string },
        {
          itemId: string;
          diameter: number;
          length: number;
          threadLength?: number | null;
          myRk?: number | null;
          ftensK?: number | null;
          headType?: string | null;
          visibility?: string | null;
        }
      >(url, body);
      mutateCache([`${BASE}/fasteners`, `${BASE.replace("/admin", "")}/fasteners`], url);
      return result;
    },
    [api],
  );

  const getFastener = useCallback(
    (id: string, signal: AbortSignal) => {
      return useSWRMutation<AdminFastener, CustomError, string>(`${BASE}/fasteners/${id}`, (url: string) =>
        api.get<AdminFastener>({ url, signal }),
      );
    },
    [api],
  );

  const updateFastener = useCallback(
    async (
      id: string,
      body: {
        diameter?: number | null;
        length?: number | null;
        threadLength?: number | null;
        myRk?: number | null;
        ftensK?: number | null;
        headType?: string | null;
        visibility?: string | null;
      },
    ): Promise<void> => {
      const url = `${BASE}/fasteners/${id}`;
      await api.put<{
        diameter?: number | null;
        length?: number | null;
        threadLength?: number | null;
        myRk?: number | null;
        ftensK?: number | null;
        headType?: string | null;
        visibility?: string | null;
      }>(url, body);
      mutateCache([`${BASE}/fasteners`, `${BASE.replace("/admin", "")}/fasteners`], url);
    },
    [api],
  );

  const deleteFastener = useCallback(
    async (id: string): Promise<void> => {
      await api.del(`${BASE}/fasteners/${id}`);
      mutateCache([`${BASE}/fasteners`, `${BASE.replace("/admin", "")}/fasteners`]);
    },
    [api],
  );

  const updateFastenerOrder = useCallback(
    async (id: string, body: { order: number }): Promise<void> => {
      const url = `${BASE}/fasteners/${id}/order`;
      await api.put(url, body);
      mutateCache([`${BASE}/fasteners`], url);
    },
    [api],
  );

  // ── Kmod Values ──

  const useKmodValues = (query: PaginationQuery, signal: AbortSignal) => {
    const url = buildUrl(`${BASE}/kmod_values`, query);
    return useSWR<{ items: AdminKmodValue[]; total: number }, CustomError, string>(url, (url: string) =>
      api.get<{ items: AdminKmodValue[]; total: number }>({ url, signal }),
    );
  };

  const getKmodValues = useCallback(
    async (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`${BASE}/kmod_values`, query);
      return api.get<{ items: AdminKmodValue[]; total: number }>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertKmodValue = useCallback(
    async (body: { serviceClassId: string; loadDurationClassId: string; kmod: number }): Promise<{ id: string }> => {
      const url = `${BASE}/kmod_values`;
      const result = await api.post<
        { id: string },
        { serviceClassId: string; loadDurationClassId: string; kmod: number }
      >(url, body);
      mutateCache([`${BASE}/kmod_values`, `${BASE.replace("/admin", "")}/kmod_values`], url);
      return result;
    },
    [api],
  );

  const getKmodValue = useCallback(
    (serviceClassId: string, loadDurationClassId: string, signal: AbortSignal) => {
      return useSWRMutation<AdminKmodValue, CustomError, string>(
        `${BASE}/kmod_values/${serviceClassId}/${loadDurationClassId}`,
        (url: string) => api.get<AdminKmodValue>({ url, signal }),
      );
    },
    [api],
  );

  const updateKmodValue = useCallback(
    async (serviceClassId: string, loadDurationClassId: string, body: { kmod: number }): Promise<void> => {
      const url = `${BASE}/kmod_values/${serviceClassId}/${loadDurationClassId}`;
      await api.put<{ kmod: number }>(url, body);
      mutateCache([`${BASE}/kmod_values`, `${BASE.replace("/admin", "")}/kmod_values`], url);
    },
    [api],
  );

  const deleteKmodValue = useCallback(
    async (serviceClassId: string, loadDurationClassId: string): Promise<void> => {
      await api.del(`${BASE}/kmod_values/${serviceClassId}/${loadDurationClassId}`);
      mutateCache([`${BASE}/kmod_values`, `${BASE.replace("/admin", "")}/kmod_values`]);
    },
    [api],
  );

  // ── GammaM Values ──

  const useGammaMValues = (query: PaginationQuery, signal: AbortSignal) => {
    const url = buildUrl(`${BASE}/gamma_m_values`, query);
    return useSWR<{ items: AdminGammaMValue[]; total: number }, CustomError, string>(url, (url: string) =>
      api.get<{ items: AdminGammaMValue[]; total: number }>({ url, signal }),
    );
  };

  const getGammaMValues = useCallback(
    async (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`${BASE}/gamma_m_values`, query);
      return api.get<{ items: AdminGammaMValue[]; total: number }>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertGammaMValue = useCallback(
    async (body: { standardId: string; continuousCtrl: boolean | null; gammaM: number }): Promise<{ id: string }> => {
      const url = `${BASE}/gamma_m_values`;
      const result = await api.post<
        { id: string },
        { standardId: string; continuousCtrl: boolean | null; gammaM: number }
      >(url, body);
      mutateCache([`${BASE}/gamma_m_values`, `${BASE.replace("/admin", "")}/gamma_m_values`], url);
      return result;
    },
    [api],
  );

  // "null" addresses the dedicated EC5 row (no continuous_ctrl concept) —
  // matches parseContinuousCtrlParam on the backend, which reads this same
  // literal from the path segment.
  const getGammaMValue = useCallback(
    (standardId: string, continuousCtrl: boolean | null, signal: AbortSignal) => {
      return useSWRMutation<AdminGammaMValue, CustomError, string>(
        `${BASE}/gamma_m_values/${standardId}/${continuousCtrl}`,
        (url: string) => api.get<AdminGammaMValue>({ url, signal }),
      );
    },
    [api],
  );

  const updateGammaMValue = useCallback(
    async (standardId: string, continuousCtrl: boolean | null, body: { gammaM: number }): Promise<void> => {
      const url = `${BASE}/gamma_m_values/${standardId}/${continuousCtrl}`;
      await api.put<{ gammaM: number }>(url, body);
      mutateCache([`${BASE}/gamma_m_values`, `${BASE.replace("/admin", "")}/gamma_m_values`], url);
    },
    [api],
  );

  const deleteGammaMValue = useCallback(
    async (standardId: string, continuousCtrl: boolean | null): Promise<void> => {
      await api.del(`${BASE}/gamma_m_values/${standardId}/${continuousCtrl}`);
      mutateCache([`${BASE}/gamma_m_values`, `${BASE.replace("/admin", "")}/gamma_m_values`]);
    },
    [api],
  );

  // ── Connectors ──

  const useLocks = (query: PaginationQuery, signal: AbortSignal) => {
    const url = buildUrl(`${BASE}/lock`, query);
    return useSWR<{ items: AdminLock[]; total: number }, CustomError, string>(url, (url: string) =>
      api.get<{ items: AdminLock[]; total: number }>({ url, signal }),
    );
  };

  const getLocks = useCallback(
    async (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`${BASE}/lock`, query);
      return api.get<{ items: AdminLock[]; total: number }>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertLock = useCallback(
    async (body: {
      itemId: string;
      lockModuleId: string;
      family: "MIDI" | "MINI";
      nModules: number;
      width: number;
      screwEdgeThickness?: number | null;
      evoItemId?: string | null;
      nInclinedUplift?: number | null;
      nInclinedLateral?: number | null;
      visibility?: string | null;
    }): Promise<{ id: string }> => {
      const url = `${BASE}/lock`;
      const result = await api.post<
        { id: string },
        {
          itemId: string;
          lockModuleId: string;
          family: "MIDI" | "MINI";
          nModules: number;
          width: number;
          screwEdgeThickness?: number | null;
          evoItemId?: string | null;
          nInclinedUplift?: number | null;
          nInclinedLateral?: number | null;
          visibility?: string | null;
        }
      >(url, body);
      mutateCache([`${BASE}/lock`, `${BASE.replace("/admin", "")}/lock`], url);
      return result;
    },
    [api],
  );

  const getLock = useCallback(
    (id: string, signal: AbortSignal) => {
      return useSWRMutation<AdminLock, CustomError, string>(`${BASE}/lock/${id}`, (url: string) =>
        api.get<AdminLock>({ url, signal }),
      );
    },
    [api],
  );

  const updateLock = useCallback(
    async (
      id: string,
      body: {
        lockModuleId?: string | null;
        family?: "MIDI" | "MINI" | null;
        nModules?: number | null;
        width?: number | null;
        screwEdgeThickness?: number | null;
        evoItemId?: string | null;
        nInclinedUplift?: number | null;
        nInclinedLateral?: number | null;
        visibility?: string | null;
      },
    ): Promise<void> => {
      const url = `${BASE}/lock/${id}`;
      await api.put<{
        lockModuleId?: string | null;
        family?: "MIDI" | "MINI" | null;
        nModules?: number | null;
        width?: number | null;
        screwEdgeThickness?: number | null;
        evoItemId?: string | null;
        nInclinedUplift?: number | null;
        nInclinedLateral?: number | null;
        visibility?: string | null;
      }>(url, body);
      mutateCache([`${BASE}/lock`, `${BASE.replace("/admin", "")}/lock`], url);
    },
    [api],
  );

  const deleteLock = useCallback(
    async (id: string): Promise<void> => {
      await api.del(`${BASE}/lock/${id}`);
      mutateCache([`${BASE}/lock`, `${BASE.replace("/admin", "")}/lock`]);
    },
    [api],
  );

  const updateLockOrder = useCallback(
    async (id: string, body: { order: number }): Promise<void> => {
      const url = `${BASE}/lock/${id}/order`;
      await api.put(url, body);
      mutateCache([`${BASE}/lock`], url);
    },
    [api],
  );

  // ── Lock Modules ──

  const useLockModules = (query: PaginationQuery, signal: AbortSignal) => {
    const url = buildUrl(`${BASE}/lock_modules`, query);
    return useSWR<{ items: AdminLockModule[]; total: number }, CustomError, string>(url, (url: string) =>
      api.get<{ items: AdminLockModule[]; total: number }>({ url, signal }),
    );
  };

  const getLockModules = useCallback(
    async (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`${BASE}/lock_modules`, query);
      return api.get<{ items: AdminLockModule[]; total: number }>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  // Mirrors InsertLockModuleRequestBody/UpdateLockModuleRequestBody in
  // internal/engines/lock_calculator/models/api/requests.go.
  const insertLockModule = useCallback(
    async (body: Omit<AdminLockModule, "id" | "coefficients">): Promise<{
      id: string;
    }> => {
      const url = `${BASE}/lock_modules`;
      const result = await api.post<{ id: string }, typeof body>(url, body);
      mutateCache([`${BASE}/lock_modules`], url);
      return result;
    },
    [api],
  );

  const updateLockModule = useCallback(
    async (
      id: string,
      body: Partial<Omit<AdminLockModule, "id" | "coefficients">>,
    ): Promise<void> => {
      const url = `${BASE}/lock_modules/${id}`;
      await api.put<typeof body>(url, body);
      mutateCache([`${BASE}/lock_modules`], url);
    },
    [api],
  );

  const deleteLockModule = useCallback(
    async (id: string): Promise<void> => {
      await api.del(`${BASE}/lock_modules/${id}`);
      mutateCache([`${BASE}/lock_modules`]);
    },
    [api],
  );

  // ── Lock Module Coefficients ──

  const useLockModuleCoefficients = (lockModuleId: string | undefined, signal: AbortSignal) => {
    const url = lockModuleId ? `${BASE}/lock_modules/${lockModuleId}/coefficients` : null;
    return useSWR<{ items: AdminLockModuleCoefficient[]; total: number }, CustomError, string | null>(
      url,
      (url: string) => api.get<{ items: AdminLockModuleCoefficient[]; total: number }>({ url, signal }),
    );
  };

  // Mirrors InsertLockModuleCoefficientRequestBody/UpdateLockModuleCoefficientRequestBody
  // in internal/engines/lock_calculator/models/api/requests.go.
  const insertLockModuleCoefficient = useCallback(
    async (
      lockModuleId: string,
      body: Omit<AdminLockModuleCoefficient, "id" | "lockModuleId">,
    ): Promise<{ id: string }> => {
      const url = `${BASE}/lock_modules/${lockModuleId}/coefficients`;
      const result = await api.post<{ id: string }, typeof body>(url, body);
      mutateCache([url], url);
      return result;
    },
    [api],
  );

  const updateLockModuleCoefficient = useCallback(
    async (
      lockModuleId: string,
      coefficientId: string,
      body: Partial<Omit<AdminLockModuleCoefficient, "id" | "lockModuleId">>,
    ): Promise<void> => {
      const url = `${BASE}/lock_modules/${lockModuleId}/coefficients/${coefficientId}`;
      await api.put<typeof body>(url, body);
      mutateCache([`${BASE}/lock_modules/${lockModuleId}/coefficients`], url);
    },
    [api],
  );

  const deleteLockModuleCoefficient = useCallback(
    async (lockModuleId: string, coefficientId: string): Promise<void> => {
      await api.del(`${BASE}/lock_modules/${lockModuleId}/coefficients/${coefficientId}`);
      mutateCache([`${BASE}/lock_modules/${lockModuleId}/coefficients`]);
    },
    [api],
  );

  // ── 3D Models ──

  const use3DModels = (modelType: Admin3DModelEntityType | undefined, query: PaginationQuery, signal: AbortSignal) => {
    const baseUrl = buildUrl(`${BASE}/3d_models`, query);
    const url = modelType ? `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}modelType=${modelType}` : baseUrl;
    return useSWR<{ items: Admin3DModelRecord[]; total: number }, CustomError, string>(url, (url: string) =>
      api.get<{ items: Admin3DModelRecord[]; total: number }>({ url, signal }),
    );
  };

  const upload3DModels = useCallback(
    async (
      entries: Admin3DModelUploadEntry[],
      onProgress?: (progress: number) => void,
    ): Promise<Admin3DModelUploadResult[]> => {
      const formData = new FormData();
      formData.append(
        "entries",
        JSON.stringify(
          entries.map(({ file: _file, ...entry }) => ({
            ...entry,
            features: entry.features ?? null,
          })),
        ),
      );
      entries.forEach((entry) => formData.append("files", entry.file, entry.file.name));
      const response = await new Promise<{ results: Admin3DModelUploadResult[] }>((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open("POST", `/api${BASE}/3d_models/bulk`);
        request.withCredentials = true;
        Object.entries(apiHeaders).forEach(([name, value]) => request.setRequestHeader(name, value));
        request.upload.onprogress = (event) => {
          if (event.lengthComputable) onProgress?.(Math.round((event.loaded / event.total) * 100));
        };
        request.onerror = () => reject(new Error("The 3D model upload request failed."));
        request.onload = () => {
          let responseBody: { results: Admin3DModelUploadResult[] } | { error?: unknown } | undefined;
          try {
            responseBody = request.responseText ? JSON.parse(request.responseText) : undefined;
          } catch {
            reject(new Error("The 3D model upload returned an invalid response."));
            return;
          }
          if (request.status >= 200 && request.status < 300) {
            resolve(responseBody as { results: Admin3DModelUploadResult[] });
            return;
          }
          if (request.status === 401) expire();
          reject(
            (responseBody as { error?: unknown } | undefined)?.error ??
              new Error(`The 3D model upload failed with status ${request.status}.`),
          );
        };
        request.send(formData);
      });
      mutateCache([`${BASE}/3d_models`]);
      return response.results;
    },
    [expire, apiHeaders],
  );

  const update3DModel = useCallback(
    async (
      modelType: Admin3DModelEntityType,
      modelId: string,
      body: {
        modelType?: Admin3DModelEntityType;
        itemId?: string | null;
        modelFile?: string;
        features?: Record<string, unknown> | null;
        file?: File;
      },
    ): Promise<Admin3DModelRecord> => {
      const formData = new FormData();
      if (body.modelType) formData.append("modelType", body.modelType);
      if (body.itemId !== undefined) formData.append("itemId", body.itemId ?? "");
      if (body.modelFile !== undefined) formData.append("modelFile", body.modelFile);
      if (body.features !== undefined) formData.append("features", JSON.stringify(body.features));
      if (body.file) formData.append("file", body.file, body.file.name);
      const url = `${BASE}/3d_models/${modelType}/${modelId}`;
      const result = await api.putWithResult<FormData, Admin3DModelRecord>(url, formData);
      mutateCache([`${BASE}/3d_models`], url);
      return result;
    },
    [api],
  );

  const delete3DModel = useCallback(
    async (modelType: Admin3DModelEntityType, modelId: string): Promise<void> => {
      await api.del(`${BASE}/3d_models/${modelType}/${modelId}`);
      mutateCache([`${BASE}/3d_models`]);
    },
    [api],
  );

  const download3DModel = useCallback(
    (model: Pick<Admin3DModelRecord, "modelType" | "modelId" | "modelFile">) =>
      api.downloadFile(`${BASE}/3d_models/${model.modelType}/${model.modelId}/file`, model.modelFile),
    [api],
  );

  const get3DModelObjectUrl = useCallback(
    (model: Pick<Admin3DModelRecord, "modelType" | "modelId">) =>
      api.createObjectURL(`/api${BASE}/3d_models/${model.modelType}/${model.modelId}/file`, true),
    [api],
  );

  const associate3DModel = useCallback(
    async (modelType: Admin3DModelEntityType, modelId: string, itemId: string): Promise<void> => {
      const url = `${BASE}/3d_models/${modelType}/${modelId}/association`;
      await api.put(url, { itemId });
      mutateCache([`${BASE}/3d_models`], url);
    },
    [api],
  );

  const detach3DModel = useCallback(
    async (modelType: Admin3DModelEntityType, modelId: string): Promise<void> => {
      const url = `${BASE}/3d_models/${modelType}/${modelId}/association`;
      await api.del(url);
      mutateCache([`${BASE}/3d_models`], url);
    },
    [api],
  );

  const useLock3D = (query: PaginationQuery, signal: AbortSignal) => use3DModels("lock_3d", query, signal);
  const useFastener3D = (query: PaginationQuery, signal: AbortSignal) => use3DModels("fastener_3d", query, signal);
  const useLockstop3D = (query: PaginationQuery, signal: AbortSignal) => use3DModels("lockstop_3d", query, signal);

  // ── Lock Accessories ──

  const useLockAccessories = (query: PaginationQuery, signal: AbortSignal) => {
    const url = buildUrl(`${BASE}/lock_accessories`, query);
    return useSWR<{ items: AdminLockAccessory[]; total: number }, CustomError, string>(url, (url: string) =>
      api.get<{ items: AdminLockAccessory[]; total: number }>({ url, signal }),
    );
  };

  const getLockAccessories = useCallback(
    async (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`${BASE}/lock_accessories`, query);
      return api.get<{ items: AdminLockAccessory[]; total: number }>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertLockAccessory = useCallback(
    async (body: { lockItemId: string; accessoryItemId: string }): Promise<{ id: string }> => {
      const url = `${BASE}/lock_accessories`;
      const result = await api.post<{ id: string }, { lockItemId: string; accessoryItemId: string }>(url, body);
      mutateCache([`${BASE}/lock_accessories`], url);
      return result;
    },
    [api],
  );

  const deleteLockAccessory = useCallback(
    async (id: string): Promise<void> => {
      await api.del(`${BASE}/lock_accessories/${id}`);
      mutateCache([`${BASE}/lock_accessories`]);
    },
    [api],
  );

  // ── Lock Stops ──

  const useLockStops = (query: PaginationQuery, signal: AbortSignal) => {
    const url = buildUrl(`${BASE}/lock_stops`, query);
    return useSWR<{ items: AdminLockStop[]; total: number }, CustomError, string>(url, (url: string) =>
      api.get<{ items: AdminLockStop[]; total: number }>({ url, signal }),
    );
  };

  const getLockStops = useCallback(
    async (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`${BASE}/lock_stops`, query);
      return api.get<{ items: AdminLockStop[]; total: number }>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertLockStop = useCallback(
    async (body: {
      itemId: string;
      fyK?: number | null;
      area?: number | null;
      fLockstopRk?: number | null;
      isSingle?: boolean | null;
      visibility?: string | null;
    }): Promise<{ id: string }> => {
      const url = `${BASE}/lock_stops`;
      const result = await api.post<
        { id: string },
        {
          itemId: string;
          fyK?: number | null;
          area?: number | null;
          fLockstopRk?: number | null;
          isSingle?: boolean | null;
          visibility?: string | null;
        }
      >(url, body);
      mutateCache([`${BASE}/lock_stops`], url);
      return result;
    },
    [api],
  );

  const getLockStop = useCallback(
    (id: string, signal: AbortSignal) => {
      return useSWRMutation<AdminLockStop, CustomError, string>(`${BASE}/lock_stops/${id}`, (url: string) =>
        api.get<AdminLockStop>({ url, signal }),
      );
    },
    [api],
  );

  const updateLockStop = useCallback(
    async (
      id: string,
      body: {
        code?: string | null;
        fyK?: number | null;
        area?: number | null;
        fLockstopRk?: number | null;
        isSingle?: boolean | null;
        visibility?: string | null;
      },
    ): Promise<void> => {
      const url = `${BASE}/lock_stops/${id}`;
      await api.put<{
        code?: string | null;
        fyK?: number | null;
        area?: number | null;
        fLockstopRk?: number | null;
        isSingle?: boolean | null;
        visibility?: string | null;
      }>(url, body);
      mutateCache([`${BASE}/lock_stops`], url);
    },
    [api],
  );

  const deleteLockStop = useCallback(
    async (id: string): Promise<void> => {
      await api.del(`${BASE}/lock_stops/${id}`);
      mutateCache([`${BASE}/lock_stops`]);
    },
    [api],
  );

  const updateLockStopOrder = useCallback(
    async (id: string, body: { order: number }): Promise<void> => {
      const url = `${BASE}/lock_stops/${id}/order`;
      await api.put(url, body);
      mutateCache([`${BASE}/lock_stops`], url);
    },
    [api],
  );

  // ── Fastener Usages (standard / inclined) ──

  const useFastenerUsages = (query: PaginationQuery, signal: AbortSignal) => {
    const url = buildUrl(`${BASE}/fastener_usages`, query);
    return useSWR<{ items: AdminFastenerUsage[]; total: number }, CustomError, string>(url, (url: string) =>
      api.get<{ items: AdminFastenerUsage[]; total: number }>({ url, signal }),
    );
  };

  const insertFastenerUsage = useCallback(
    async (body: { fastenerItemId: string; usage: string }): Promise<void> => {
      const url = `${BASE}/fastener_usages`;
      await api.post<void, { fastenerItemId: string; usage: string }>(url, body);
      mutateCache([`${BASE}/fastener_usages`, `${BASE}/fasteners`], url);
    },
    [api],
  );

  const deleteFastenerUsage = useCallback(
    async (fastenerItemId: string, usage: string): Promise<void> => {
      await api.del(`${BASE}/fastener_usages/${fastenerItemId}/${usage}`);
      mutateCache([`${BASE}/fastener_usages`, `${BASE}/fasteners`]);
    },
    [api],
  );

  // ── Lock → Lockstop Mappings ──

  const useLockLockstopMappings =(query: PaginationQuery, signal: AbortSignal) => {
    const url = buildUrl(`${BASE}/lock_lockstop_mappings`, query);
    return useSWR<{ items: AdminLockLockstopMapping[]; total: number }, CustomError, string>(url, (url: string) =>
      api.get<{ items: AdminLockLockstopMapping[]; total: number }>({ url, signal }),
    );
  };

  const getLockLockstopMappings = useCallback(
    async (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`${BASE}/lock_lockstop_mappings`, query);
      return api.get<{ items: AdminLockLockstopMapping[]; total: number }>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertLockLockstopMapping = useCallback(
    async (body: {
      lockItemId: string;
      lockstopItemId: string;
      quantity: number;
      installationLevels: number;
    }): Promise<void> => {
      const url = `${BASE}/lock_lockstop_mappings`;
      await api.post<
        void,
        { lockItemId: string; lockstopItemId: string; quantity: number; installationLevels: number }
      >(url, body);
      mutateCache([`${BASE}/lock_lockstop_mappings`], url);
    },
    [api],
  );

  const updateLockLockstopMapping = useCallback(
    async (
      lockItemId: string,
      lockstopItemId: string,
      body: { quantity: number; installationLevels: number },
    ): Promise<void> => {
      await api.put(`${BASE}/lock_lockstop_mappings/${lockItemId}/${lockstopItemId}`, body);
      mutateCache([`${BASE}/lock_lockstop_mappings`]);
    },
    [api],
  );

  const deleteLockLockstopMapping = useCallback(
    async (lockItemId: string, lockstopItemId: string): Promise<void> => {
      await api.del(`${BASE}/lock_lockstop_mappings/${lockItemId}/${lockstopItemId}`);
      mutateCache([`${BASE}/lock_lockstop_mappings`]);
    },
    [api],
  );

  // ── Lock pair → Lockstop Mappings ──
  // Con 2 connettori affiancati valgono solo queste righe (la coppia e' non ordinata).

  const useLockPairLockstopMappings = (query: PaginationQuery, signal: AbortSignal) => {
    const url = buildUrl(`${BASE}/lock_pair_lockstop_mappings`, query);
    return useSWR<{ items: AdminLockPairLockstopMapping[]; total: number }, CustomError, string>(url, (url: string) =>
      api.get<{ items: AdminLockPairLockstopMapping[]; total: number }>({ url, signal }),
    );
  };

  const insertLockPairLockstopMapping = useCallback(
    async (body: {
      lockAItemId: string;
      lockBItemId: string;
      lockstopItemId: string;
      quantity: number;
      installationLevels: number;
    }): Promise<void> => {
      const url = `${BASE}/lock_pair_lockstop_mappings`;
      // L'endpoint risponde 201 senza body: `api.post` farebbe `.json()` e fallirebbe dopo aver creato la riga.
      await api.postNoResult(url, body);
      mutateCache([`${BASE}/lock_pair_lockstop_mappings`], url);
    },
    [api],
  );

  const updateLockPairLockstopMapping = useCallback(
    async (
      lockAItemId: string,
      lockBItemId: string,
      lockstopItemId: string,
      body: { quantity: number; installationLevels: number },
    ): Promise<void> => {
      await api.put(`${BASE}/lock_pair_lockstop_mappings/${lockAItemId}/${lockBItemId}/${lockstopItemId}`, body);
      mutateCache([`${BASE}/lock_pair_lockstop_mappings`]);
    },
    [api],
  );

  const deleteLockPairLockstopMapping = useCallback(
    async (lockAItemId: string, lockBItemId: string, lockstopItemId: string): Promise<void> => {
      await api.del(`${BASE}/lock_pair_lockstop_mappings/${lockAItemId}/${lockBItemId}/${lockstopItemId}`);
      mutateCache([`${BASE}/lock_pair_lockstop_mappings`]);
    },
    [api],
  );

  return {
    // Standards
    useStandards,
    getStandards,
    insertStandard,
    getStandard,
    updateStandard,
    deleteStandard,
    updateStandardOrder,
    // Service Classes
    useServiceClasses,
    getServiceClasses,
    insertServiceClass,
    getServiceClass,
    updateServiceClass,
    deleteServiceClass,
    // Load Duration Classes
    useLoadDurationClasses,
    getLoadDurationClasses,
    insertLoadDurationClass,
    getLoadDurationClass,
    updateLoadDurationClass,
    deleteLoadDurationClass,
    // Material Grades
    useMaterialGrades,
    getMaterialGrades,
    insertMaterialGrade,
    getMaterialGrade,
    updateMaterialGrade,
    deleteMaterialGrade,
    updateMaterialGradeOrder,
    // Fasteners
    useFasteners,
    getFasteners,
    insertFastener,
    getFastener,
    updateFastener,
    deleteFastener,
    updateFastenerOrder,
    // Kmod Values
    useKmodValues,
    getKmodValues,
    insertKmodValue,
    getKmodValue,
    updateKmodValue,
    deleteKmodValue,
    // GammaM Values
    useGammaMValues,
    getGammaMValues,
    insertGammaMValue,
    getGammaMValue,
    updateGammaMValue,
    deleteGammaMValue,
    // Connectors
    useLocks,
    getLocks,
    insertLock,
    getLock,
    updateLock,
    deleteLock,
    updateLockOrder,
    // Connector Elements
    useLockModules,
    getLockModules,
    insertLockModule,
    updateLockModule,
    deleteLockModule,
    useLockModuleCoefficients,
    insertLockModuleCoefficient,
    updateLockModuleCoefficient,
    deleteLockModuleCoefficient,
    // 3D Models
    use3DModels,
    useLock3D,
    useFastener3D,
    useLockstop3D,
    upload3DModels,
    update3DModel,
    delete3DModel,
    download3DModel,
    get3DModelObjectUrl,
    associate3DModel,
    detach3DModel,
    // Connector Accessories
    useLockAccessories,
    getLockAccessories,
    insertLockAccessory,
    deleteLockAccessory,
    // Lock Stops
    useLockStops,
    getLockStops,
    insertLockStop,
    getLockStop,
    updateLockStop,
    deleteLockStop,
    updateLockStopOrder,
    // Lock Module → Lockstop Mappings
    useFastenerUsages,
    insertFastenerUsage,
    deleteFastenerUsage,
    useLockLockstopMappings,
    getLockLockstopMappings,
    insertLockLockstopMapping,
    updateLockLockstopMapping,
    deleteLockLockstopMapping,
    // Lock pair → Lockstop Mappings
    useLockPairLockstopMappings,
    insertLockPairLockstopMapping,
    updateLockPairLockstopMapping,
    deleteLockPairLockstopMapping,
  };
};
